import { provider } from "@/provider.js"
import { prepMessageForStore, buildPendingUserMessage } from "./helpers.js"

export default {
  async getAllSessions({ commit }) {
    commit("loadingStarted", "allSessions")
    const response = await provider.getAllChatSessions()
    if (!response.error) {
      commit("storeAllSessions", response)
    } else {
      commit("displayFlashMessage", { message: response.message, severity: "error" })
    }
    commit("loadingEnded", "allSessions")
    return response
  },
  async getChatSession({ commit }, id) {
    commit("loadingStarted", "session")
    const response = await provider.getChatSession(id)
    commit("storeSession", response)
    commit("loadingEnded", "session")
  },
  async createChatSession({ commit }) {
    commit("loadingStarted", "session")
    const chatSession = await provider.createChatSession({ title: "Chat Session" })
    if (chatSession.messages && chatSession.messages.length) {
      chatSession.messages = await Promise.all(chatSession.messages.map(prepMessageForStore))
    }
    commit("appendSession", chatSession)
    commit("storeSession", chatSession)
    commit("loadingEnded", "session")
    return chatSession
  },
  async deleteChatSession({ commit, state }, sessionId) {
    try {
      await provider.deleteChatSession(sessionId)
      const id = typeof sessionId === "number" ? sessionId : parseInt(sessionId, 10)
      commit("removeSession", id)
      if (state.currentSession && Number(state.currentSession.id) === id) {
        commit("clearCurrentSession")
      }
      return true
    } catch (err) {
      const message = (err.response && err.response.message) || err.message || "Unable to delete chat session."
      commit("displayFlashMessage", { message, severity: "error" })
      throw err
    }
  },
  async updateChatSessionTitle({ commit }, { sessionId, title }) {
    try {
      const chatSession = await provider.updateChatSession(sessionId, title)
      commit("updateSessionInList", chatSession)
      return chatSession
    } catch (err) {
      const message = (err.response && err.response.message) || err.message || "Unable to rename chat session."
      commit("displayFlashMessage", { message, severity: "error" })
      throw err
    }
  },
  clearCurrentChatSession({commit}) {
    commit("clearCurrentSession")
  },
  async getChatMessagesForSession({commit}, sessionId) {
    commit("loadingStarted", "messages")
    const messages = await provider.getChatMessagesForSession(sessionId)
    const prepped = await Promise.all((messages.chatMessages || []).map(prepMessageForStore))
    const plain = prepped.map((m) => {
      try {
        return JSON.parse(JSON.stringify(m))
      } catch (e) {
        console.warn("[Chat] Could not strip reactivity from message", m.id, e)
        return m
      }
    })
    commit("storeMessages", { messages: plain, prepped: true })
    commit("loadingEnded", "messages")
    return messages
  },
  async updateChatMessagesForSession({commit}, sessionId) {
    const messages = await provider.getChatMessagesForSession(sessionId)
    const prepped = await Promise.all((messages.chatMessages || []).map(prepMessageForStore))
    const plain = prepped.map((m) => {
      try {
        return JSON.parse(JSON.stringify(m))
      } catch (e) {
        console.warn("[Chat] Could not strip reactivity from message", m.id, e)
        return m
      }
    })
    commit("storeMessages", { messages: plain, prepped: true })
    return messages
  },
  /**
   * Refetch messages for the session and merge into store without replacing unchanged messages.
   * Used when session subscription fires so the UI updates without repainting existing messages.
   */
  async mergeMessagesFromSessionRefetch({ commit, state }, sessionId) {
    if (!state.currentSession || state.currentSession.id !== sessionId) return
    const messages = await provider.getChatMessagesForSession(sessionId)
    const prepped = await Promise.all((messages.chatMessages || []).map(prepMessageForStore))
    const plain = prepped.map((m) => {
      try {
        return JSON.parse(JSON.stringify(m))
      } catch (e) {
        console.warn("[Chat] Could not strip reactivity from message", m.id, e)
        return m
      }
    })
    commit("mergeMessages", { messages: plain })
    return messages
  },
  async createChatMessage({ commit }, { text, sessionId }) {
    try {
      console.log("[Sending Message]", text, sessionId)
      const chatMessage = await provider.createChatMessage(text, sessionId)
      console.log("[Sending Message]", "Sent message to provider")
      return chatMessage
    } catch (err) {
      const message = (err.response && err.response.message) || err.message || "Unable to send message. Please try again."
      console.error("[Chat] createChatMessage ERROR", err)
      commit("displayFlashMessage", { message, severity: "error" })
      throw err
    }
  },
  /**
   * Show the user's message immediately (optimistic), then send to the API.
   * Replaces the placeholder when the mutation or WebSocket delivers the real message.
   */
  async sendChatMessage({ commit, dispatch, rootState }, { text, sessionId }) {
    const trimmed = (text || "").trim()
    if (!trimmed || sessionId === undefined || sessionId === null) {
      return null
    }

    const me = rootState.currentUser && rootState.currentUser.me
    if (!me || !me.id) {
      return dispatch("createChatMessage", { text: trimmed, sessionId })
    }

    const clientId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    const pending = buildPendingUserMessage(trimmed, me, clientId)
    commit("addPendingMessage", pending)

    try {
      const chatMessage = await provider.createChatMessage(trimmed, sessionId)
      if (chatMessage && chatMessage.id) {
        let plain
        try {
          plain = JSON.parse(JSON.stringify(chatMessage))
        } catch (e) {
          plain = chatMessage
        }
        const prepped = await prepMessageForStore(plain)
        commit("confirmPendingMessage", { clientId, message: prepped })
      }
      return chatMessage
    } catch (err) {
      commit("removePendingMessage", clientId)
      const message =
        (err.response && err.response.message) || err.message || "Unable to send message. Please try again."
      console.error("[Chat] sendChatMessage ERROR", err)
      commit("displayFlashMessage", { message, severity: "error" })
      throw err
    }
  },
  async sendFirstQueuedMessage({ state, commit, dispatch }) {
    if (state.sendingFromQueue) return
    if (!state.messageQueue || state.messageQueue.length === 0) return
    if (!state.currentSession || state.currentSession.id === undefined) return
    commit("setSendingFromQueue", true)
    try {
      const text = state.messageQueue[0]
      commit("removeQueuedMessage", 0)
      await dispatch("sendChatMessage", { text, sessionId: state.currentSession.id })
    } finally {
      commit("setSendingFromQueue", false)
    }
  },
  async subscribeToChatMessages({ commit }, id) {
    let success = true
    const result = await provider.subscribeToChatMessages(id)
    if (result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "error",
      })
      success = false
    }
    return success
  },
  async unsubscribeFromChatMessages({ commit }, id) {
    const success = true
    await provider.unsubscribeFromChatMessages(id)
    return success
  },
  async subscribeToChatSession({ commit }, id) {
    let success = true
    const result = await provider.subscribeToChatSession(id)
    if (result.error) {
      commit("displayFlashMessage", {
        message: result.message,
        severity: "error",
      })
      success = false
    }
    return success
  },
  async unsubscribeFromChatSession({ commit }, id) {
    const success = true
    await provider.unsubscribeFromChatSession(id)
    return success
  },
  async reconnectChatSubscriptions({ commit }, id) {
    try {
      await provider.reconnectChatSubscriptions(id)
      return true
    } catch (e) {
      console.error("[Chat WS] reconnectChatSubscriptions failed", e)
      commit("displayFlashMessage", {
        message: (e && e.message) || "Unable to reconnect chat updates",
        severity: "error",
      })
      return false
    }
  },
  setChatEnabled({ commit }, chatEnabled) {
    commit("setChatEnabled", chatEnabled)
  },
  updateMessageCreating({ commit }, chatMessageCreating) {
    console.log("chatMessageCreating", chatMessageCreating)
    if (chatMessageCreating.isBot) {
      commit("setBotCreatingMessage", true)
    } else {
      commit("setUserCreatingMessage", true)
    }
  },
  clearUserMessageCreating({ commit }) {
    commit("setUserCreatingMessage", false)
  },
  clearSystemMessageCreating({ commit }) {
    commit("setBotCreatingMessage", false)
  },
  async addMessage({ commit, state }, message) {
    console.log("[Chat] addMessage", { messageId: message && message.id, text: message && message.text && message.text.substring(0, 50) })
    let plain
    try {
      plain = JSON.parse(JSON.stringify(message))
    } catch (e) {
      console.warn("[Chat] Could not strip reactivity from message", message && message.id, e)
      plain = message
    }
    const prepped = await prepMessageForStore(plain)
    if (prepped.author && !prepped.author.isBot) {
      const pendingIdx = (state.messages.all || []).findIndex(
        (m) => m.__pending && m.author && !m.author.isBot
      )
      if (pendingIdx >= 0) {
        commit("confirmPendingMessage", { clientId: state.messages.all[pendingIdx].clientId, message: prepped })
        return
      }
    }
    if ((state.messages.all || []).some((m) => m.id === prepped.id)) {
      return
    }
    commit("addMessage", prepped)
  },
  async updateMessage({commit}, message) {
    console.log("[Chat] updateMessage", { messageId: message && message.id })
    let plain
    try {
      plain = JSON.parse(JSON.stringify(message))
    } catch (e) {
      console.warn("[Chat] Could not strip reactivity from message", message && message.id, e)
      plain = message
    }
    const prepped = await prepMessageForStore(plain)
    commit("updateMessage", prepped)
  },
  deleteMessage({commit}, message) {
    commit("deleteMessage", message)
  },
  async addChatMessageStatus({ commit }, { id, status }) {
    const chatMessage = await provider.addChatMessageStatus(id, status)
    if (chatMessage) {
      let plain
      try {
        plain = JSON.parse(JSON.stringify(chatMessage))
      } catch (e) {
        console.warn("[Chat] Could not strip reactivity from message", chatMessage.id, e)
        plain = chatMessage
      }
      const prepped = await prepMessageForStore(plain)
      commit("updateMessage", prepped)
    }
    return chatMessage
  },
}
